import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { Bot, Lightbulb, RefreshCw, GraduationCap, ArrowUpRight } from 'lucide-react';

const services = [
  {
    code: 'SVC.01',
    icon: Bot,
    title: 'AI Automation Solutions',
    description: 'We help businesses increase revenue and efficiency through intelligent automation systems — from AI agents to real-time customer engagement tools.',
  },
  {
    code: 'SVC.02',
    icon: Lightbulb,
    title: 'Prompt Engineering & AI Strategy',
    description: 'We design high-performance prompt systems that unlock the full potential of AI for business growth, productivity, and decision-making.',
  },
  {
    code: 'SVC.03',
    icon: RefreshCw,
    title: 'Digital Transformation Consulting',
    description: 'We guide organizations through adopting AI, optimizing workflows, and building future-ready systems.',
  },
  {
    code: 'SVC.04',
    icon: GraduationCap,
    title: 'AI Education & Empowerment',
    description: 'We are committed to bridging the AI knowledge gap by providing accessible education and tools to underserved communities — especially across Africa.',
  },
];

export function ServicesSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section
      id="services"
      className="relative py-24 md:py-32 overflow-hidden"
      data-testid="services-section"
    >
      <div ref={ref} className="relative max-w-[1400px] mx-auto px-6 md:px-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="text-left mb-16 max-w-3xl"
        >
          <p className="eyebrow section-eyebrow-line mb-6">
            What We Do
          </p>
          <h2 className="font-heading text-3xl md:text-5xl font-bold text-stark tracking-tight">
            Transforming Ideas Into{' '}
            <span className="text-gradient">Intelligent Solutions</span>
          </h2>
        </motion.div>

        {/* Services Bento Grid — border-collapse style */}
        <div className="grid md:grid-cols-2 gap-px bg-line-1">
          {services.map((service, index) => (
            <motion.div
              key={service.title}
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: index * 0.08, ease: [0.16, 1, 0.3, 1] }}
              className="service-card p-8 md:p-10 cursor-pointer relative"
              data-testid={`service-card-${index}`}
            >
              <div className="flex items-start justify-between mb-6">
                <span className="eyebrow">{service.code}</span>
                <ArrowUpRight className="w-5 h-5 text-stark-3 group-hover:text-stark transition-colors duration-300 flex-shrink-0" />
              </div>

              <div className="w-11 h-11 rounded-md bg-ink-2 border border-line-1 flex items-center justify-center mb-6">
                <service.icon className="w-5 h-5 text-stark" />
              </div>

              <h3 className="font-heading text-xl md:text-2xl font-semibold text-stark mb-3 tracking-tight">
                {service.title}
              </h3>
              <p className="text-stark-2 leading-relaxed">
                {service.description}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default ServicesSection;
